// useBuildingOverview — the building read endpoint (Phase 2, 2026-07-17).
//
// Flat-fetch + IN-lookup + client-join (house standard — no PostgREST embeds).
// Fetches: building row, member facilities, accounts, facility_details_* sqft,
// service_assignments lines (member lines PLUS lines anchored to a member via
// cost_group_facility_id — OPR / Remedy portfolio style), facility_feedback
// summaries, the building's shared display default, and the caller's personal
// override. READ-ONLY over billing; the only writes this module ever offers
// are display prefs (see setBuildingDisplayDefault / setMyDisplayOverride).

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../supabase';
import {
  buildingRunningTotal,
  computeBillingRollup,
  hasSharedInvoice,
  type RollupMemberInfo,
} from './rollup';
import {
  BUILDING_ACCOUNTS_SELECT,
  BUILDING_FACILITIES_SELECT,
  BUILDING_FEEDBACK_SELECT,
  BUILDING_LINES_SELECT,
  BUILDING_OUTSIDE_FACILITY_SELECT,
  BUILDING_SQFT_SELECT,
  PROVIDER_NAME_SELECT,
} from './queries';
import { parseSuite, resolveDisplayMode } from './display-mode';
import type {
  BuildingBillingLine,
  BuildingMember,
  BuildingOverview,
  BuildingRow,
  ResolvedDisplayMode,
} from './types';
import { errorMessage } from '../errors';

// 2026-09-17 — this used to be three tables, fanned out and merged back here,
// because a building's square footage lived in whichever one its facility_type
// routed to. It is one table now, so the rollup is one query.

const OPEN_FEEDBACK_STATUSES_EXCLUDED = ['Resolved', 'Closed'];

interface FacilityRowRaw {
  facility_id: string;
  facility_name: string;
  facility_type: string | null;
  facility_address: string | null;
  account_id: string | null;
  account_manager_eid: string | null;
  status: string | null;
  account_status_rag: string | null;
  is_pinned: boolean | null;
  is_common_area: boolean | null;
  monthly_rate: number | string | null;
  management_fee_pct: number | string | null;
}

function toNum(v: number | string | null): number | null {
  if (v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export interface UseBuildingOverviewResult {
  overview: BuildingOverview | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  // Free-edit display prefs (§3). Building default = shared; override = mine.
  setBuildingDisplayDefault: (mode: 'auto' | ResolvedDisplayMode) => Promise<void>;
  setMyDisplayOverride: (mode: ResolvedDisplayMode | null) => Promise<void>;
}

export function useBuildingOverview(buildingId: string | null): UseBuildingOverviewResult {
  const [overview, setOverview] = useState<BuildingOverview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  const refresh = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    if (!buildingId) {
      setOverview(null);
      return;
    }
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError(null);
      try {
        // 1. Building + members (parallel with auth uid)
        const [{ data: bldg, error: bErr }, { data: auth }] = await Promise.all([
          supabase.from('buildings').select('*').eq('building_id', buildingId).maybeSingle(),
          supabase.auth.getUser(),
        ]);
        if (bErr) throw bErr;
        if (!bldg) throw new Error(`Building ${buildingId} not found`);

        const { data: facRows, error: fErr } = await supabase
          .from('facilities')
          .select(BUILDING_FACILITIES_SELECT)
          .eq('building_id', buildingId)
          .order('facility_id');
        if (fErr) throw fErr;
        // Cast via unknown: the multi-line select string defeats the supabase
        // type parser (GenericStringError) — runtime shape is FacilityRowRaw.
        const facs = (facRows ?? []) as unknown as FacilityRowRaw[];
        const facIds = facs.map((f) => f.facility_id);

        // 2. Parallel lookups: accounts, billing lines (member lines OR lines
        //    anchored to a member), feedback, sqft, my override.
        const accountIds = [...new Set(facs.map((f) => f.account_id).filter(Boolean))] as string[];
        const uid = auth?.user?.id ?? null;

        const orFilter =
          `facility_id.in.(${facIds.join(',')}),cost_group_facility_id.in.(${facIds.join(',')})`;

        const [accountsRes, linesRes, feedbackRes, prefRes, detailRes] = await Promise.all([
          accountIds.length
            ? supabase.from('accounts').select(BUILDING_ACCOUNTS_SELECT).in('account_id', accountIds)
            : Promise.resolve({ data: [], error: null }),
          facIds.length
            ? supabase
                .from('service_assignments')
                .select(BUILDING_LINES_SELECT)
                .or(orFilter)
            : Promise.resolve({ data: [], error: null }),
          facIds.length
            ? supabase
                .from('facility_feedback')
                .select(BUILDING_FEEDBACK_SELECT)
                .in('facility_id', facIds)
            : Promise.resolve({ data: [], error: null }),
          uid
            ? supabase
                .from('user_building_view_prefs')
                .select('display_mode')
                .eq('user_id', uid)
                .eq('building_id', buildingId)
                .maybeSingle()
            : Promise.resolve({ data: null, error: null }),
          facIds.length
            ? supabase
                .from('facility_details')
                .select(BUILDING_SQFT_SELECT)
                .in('facility_id', facIds)
            : Promise.resolve({ data: [], error: null }),
        ]);

        // Defensive: billing/feedback failures must not blank the card — but
        // they are said, not swallowed (a silent [] reads as "no lines").
        for (const [what, res] of [
          ['accounts', accountsRes],
          ['billing lines', linesRes],
          ['feedback', feedbackRes],
          ['square feet', detailRes],
        ] as const) {
          if (res.error) console.warn(`[building-overview] ${what} lookup failed`, res.error);
        }
        const accountNameById = new Map<string, string>(
          ((accountsRes.data ?? []) as { account_id: string; account_name: string }[]).map(
            (a) => [a.account_id, a.account_name],
          ),
        );
        const lines = (linesRes.error
          ? []
          : (linesRes.data ?? [])) as unknown as BuildingBillingLine[];
        const feedback = (feedbackRes.error ? [] : (feedbackRes.data ?? [])) as {
          facility_id: string | null;
          status: string | null;
          feedback_date: string | null;
        }[];

        const sqftByFacility = new Map<string, number>();
        for (const row of (detailRes.error ? [] : (detailRes.data ?? [])) as {
          facility_id: string;
          total_sqft: number | string | null;
        }[]) {
          const n = toNum(row.total_sqft);
          if (n != null) sqftByFacility.set(row.facility_id, n);
        }

        const feedbackByFacility = new Map<string, { open: number; last: string | null }>();
        for (const fb of feedback) {
          if (!fb.facility_id) continue;
          const entry = feedbackByFacility.get(fb.facility_id) ?? { open: 0, last: null };
          if (fb.status && !OPEN_FEEDBACK_STATUSES_EXCLUDED.includes(fb.status)) entry.open += 1;
          if (fb.feedback_date && (!entry.last || fb.feedback_date > entry.last)) {
            entry.last = fb.feedback_date;
          }
          feedbackByFacility.set(fb.facility_id, entry);
        }

        // 3. Assemble members
        const linesByFacility = new Map<string, BuildingBillingLine[]>();
        for (const line of lines) {
          if (!line.facility_id) continue;
          const arr = linesByFacility.get(line.facility_id) ?? [];
          arr.push(line);
          linesByFacility.set(line.facility_id, arr);
        }

        const members: BuildingMember[] = facs.map((f) => ({
          facility_id: f.facility_id,
          facility_name: f.facility_name,
          facility_type: f.facility_type,
          facility_address: f.facility_address,
          suite: parseSuite(f.facility_address),
          account_id: f.account_id,
          account_name: f.account_id ? accountNameById.get(f.account_id) ?? null : null,
          account_manager_eid: f.account_manager_eid,
          status: f.status,
          account_status_rag: f.account_status_rag,
          is_pinned: f.is_pinned === true,
          is_common_area: f.is_common_area === true,
          monthly_rate: toNum(f.monthly_rate),
          management_fee_pct: toNum(f.management_fee_pct),
          total_sqft: sqftByFacility.get(f.facility_id) ?? null,
          billing_lines: (linesByFacility.get(f.facility_id) ?? []).filter(
            (l) => l.assignment_status === 'Active',
          ),
          open_feedback_count: feedbackByFacility.get(f.facility_id)?.open ?? 0,
          last_feedback_date: feedbackByFacility.get(f.facility_id)?.last ?? null,
        }));

        // 4. Resolution + rollup (pure fns)
        const tenantCount = new Set(members.map((m) => m.account_id).filter(Boolean)).size;
        const hasPinned = members.some((m) => m.is_pinned);
        const userOverride =
          (prefRes.data as { display_mode: ResolvedDisplayMode } | null)?.display_mode ?? null;
        const { mode, source } = resolveDisplayMode({
          userOverride,
          buildingDefault: (bldg as BuildingRow).display_mode,
          tenantCount,
          hasPinnedMember: hasPinned,
        });

        const rollupMembers: RollupMemberInfo[] = members.map((m) => ({
          facility_id: m.facility_id,
          account_id: m.account_id,
          account_name: m.account_name,
          management_fee_pct: m.management_fee_pct,
        }));
        const rollupGroups = computeBillingRollup(
          lines,
          rollupMembers,
          new Set(facIds),
          accountNameById,
        );

        const sqftVals = members.map((m) => m.total_sqft).filter((v): v is number => v != null);

        // 5. Names for the billing box (item 552, 2026-10-19). A billing group
        //    can span buildings (Wellstone's covers five), and the box listed
        //    those facilities as bare ids. Each line also names its cleaner.
        const facilityNames: Record<string, string> = {};
        for (const m of members) facilityNames[m.facility_id] = m.facility_name;
        const shownIds = new Set<string>();
        for (const g of rollupGroups) {
          shownIds.add(g.anchor_facility_id);
          for (const l of g.lines) if (l.facility_id) shownIds.add(l.facility_id);
        }
        const outsideIds = [...shownIds].filter((id) => !(id in facilityNames));
        const providerIds = [
          ...new Set(rollupGroups.flatMap((g) => g.lines.map((l) => l.provider_id)).filter((p): p is string => !!p)),
        ];
        const [outsideRes, providerRes] = await Promise.all([
          outsideIds.length
            ? supabase.from('facilities').select(BUILDING_OUTSIDE_FACILITY_SELECT).in('facility_id', outsideIds)
            : Promise.resolve({ data: [], error: null }),
          providerIds.length
            ? supabase.from('service_providers').select(PROVIDER_NAME_SELECT).in('provider_id', providerIds)
            : Promise.resolve({ data: [], error: null }),
        ]);
        if (outsideRes.error) console.warn('[building-overview] facility name lookup failed', outsideRes.error);
        if (providerRes.error) console.warn('[building-overview] cleaner name lookup failed', providerRes.error);
        for (const f of (outsideRes.data ?? []) as { facility_id: string; facility_name: string | null }[]) {
          if (f.facility_name) facilityNames[f.facility_id] = f.facility_name;
        }
        const providerNames: Record<string, string> = {};
        for (const p of (providerRes.data ?? []) as { provider_id: string; partner_name: string | null }[]) {
          if (p.partner_name?.trim()) providerNames[p.provider_id] = p.partner_name.trim();
        }

        if (!cancelled) {
          setOverview({
            building: bldg as BuildingRow,
            members,
            member_count: members.length,
            tenant_count: tenantCount,
            suite_count: members.filter((m) => m.suite != null).length,
            total_sqft: sqftVals.length ? sqftVals.reduce((a, b) => a + b, 0) : null,
            resolved_display_mode: mode,
            display_mode_source: source,
            rollup_groups: rollupGroups,
            building_total: buildingRunningTotal(lines, new Set(facIds)),
            has_shared_invoice: hasSharedInvoice(rollupGroups),
            facility_names: facilityNames,
            provider_names: providerNames,
          });
        }
      } catch (e) {
        if (!cancelled) setError(errorMessage(e, 'Failed to load building'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [buildingId, nonce]);

  // Free-edit display prefs (§3): metadata only, never billing.
  const setBuildingDisplayDefault = useCallback(
    async (mode: 'auto' | ResolvedDisplayMode) => {
      if (!buildingId) return;
      const { error: err } = await supabase
        .from('buildings')
        .update({ display_mode: mode })
        .eq('building_id', buildingId);
      if (err) throw err;
      refresh();
    },
    [buildingId, refresh],
  );

  const setMyDisplayOverride = useCallback(
    async (mode: ResolvedDisplayMode | null) => {
      if (!buildingId) return;
      const { data: auth } = await supabase.auth.getUser();
      const uid = auth?.user?.id;
      if (!uid) throw new Error('Not signed in');
      if (mode == null) {
        const { error: err } = await supabase
          .from('user_building_view_prefs')
          .delete()
          .eq('user_id', uid)
          .eq('building_id', buildingId);
        if (err) throw err;
      } else {
        const { error: err } = await supabase
          .from('user_building_view_prefs')
          .upsert({ user_id: uid, building_id: buildingId, display_mode: mode });
        if (err) throw err;
      }
      refresh();
    },
    [buildingId, refresh],
  );

  return { overview, loading, error, refresh, setBuildingDisplayDefault, setMyDisplayOverride };
}
